import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../App";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { LinkedAvatar, LinkedName } from "./LinkedProfile";

const API = '/api';

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const InsightConversation = ({ requestId, collapsed: initialCollapsed = true, responderName, responderPicture, responderUsername }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(!initialCollapsed);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/insights/${requestId}/messages`, { credentials: 'include' });
      if (res.ok) setMessages(await res.json());
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (isOpen) fetchMessages();
  }, [isOpen, fetchMessages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);


  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API}/insights/${requestId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage.trim() })
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setNewMessage("");
        inputRef.current?.focus();
      } else {
        toast.error('Failed to send message');
      }
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleToggle = () => {
    setIsOpen(prev => !prev);
  };

  return (
    <div className="mt-4" data-testid={`insight-conversation-${requestId}`}>
      {/* Toggle button */}
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors"
        data-testid={`conversation-toggle-${requestId}`}
      >
        <MessageSquare className="w-4 h-4" />
        <span>
          {isOpen ? 'Hide Discussion' : 'Discuss'}
          {!isOpen && messages.length > 0 && ` (${messages.length})`}
        </span>
      </button>

      {isOpen && (
        <div
          className="mt-3 rounded-xl overflow-hidden border border-white/10 bg-white/[0.03] backdrop-blur-sm"
          data-testid={`conversation-panel-${requestId}`}
        >
          {/* Messages area */}
          <div className="max-h-72 overflow-y-auto px-4 py-3 space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-5 h-5 border-2 border-violet-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-6">
                {responderName ? (
                  <>
                    <LinkedAvatar username={responderUsername} className="inline-block mx-auto mb-2">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={responderPicture} alt={responderName} />
                        <AvatarFallback className="bg-violet-500/20 text-violet-300">{responderName.charAt(0)}</AvatarFallback>
                      </Avatar>
                    </LinkedAvatar>
                    <p className="text-sm text-slate-300">
                      Do you want to ask{" "}
                      <LinkedName username={responderUsername} name={responderName} className="font-semibold text-white">
                        {responderName}
                      </LinkedName>
                      {" "}any questions or comments about this insight?
                    </p>
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-300">Start a discussion about this insight</p>
                    <p className="text-xs text-slate-500 mt-1">Ask follow-up questions or share your thoughts</p>
                  </>
                )}
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.sender_id === user?.user_id;
                return (
                  <div
                    key={msg.message_id}
                    className={`flex gap-2.5 ${isMe ? 'flex-row-reverse' : ''}`}
                    data-testid={`message-${msg.message_id}`}
                  >
                    <LinkedAvatar username={isMe ? undefined : msg.sender_username} className="flex-shrink-0 mt-0.5">
                      <Avatar className="h-7 w-7">
                        <AvatarImage src={msg.sender_picture} alt={msg.sender_name} />
                        <AvatarFallback className={`text-xs ${isMe ? 'bg-violet-500/20 text-violet-300' : 'bg-white/10 text-slate-300'}`}>
                          {msg.sender_name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                    </LinkedAvatar>
                    <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
                      <div
                        className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                          isMe
                            ? 'bg-violet-600 text-white rounded-tr-md'
                            : 'bg-white/[0.06] border border-white/10 text-slate-100 rounded-tl-md'
                        }`}
                      >
                        {!isMe && (
                          <p className="text-xs font-medium text-violet-300 mb-0.5">
                            <LinkedName username={msg.sender_username} name={msg.sender_name}>
                              {msg.sender_name}
                            </LinkedName>
                          </p>
                        )}
                        {msg.content}
                      </div>
                      <p className={`text-[10px] text-slate-500 mt-0.5 ${isMe ? 'text-right' : ''}`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <form onSubmit={sendMessage} className="flex items-center gap-2 p-3 border-t border-white/10 bg-white/[0.02]">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 text-sm bg-white/5 border border-white/10 rounded-full px-4 py-2 text-white placeholder:text-slate-500 outline-none focus:border-violet-400/50 focus:ring-1 focus:ring-violet-400/30 transition-colors"
              data-testid={`conversation-input-${requestId}`}
            />
            <Button
              type="submit"
              size="sm"
              disabled={!newMessage.trim() || sending}
              className="bg-violet-600 hover:bg-violet-700 text-white rounded-full h-9 w-9 p-0 flex-shrink-0"
              data-testid={`conversation-send-${requestId}`}
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default InsightConversation;
